import { put, take, fork, select, call } from "redux-saga/effects";
import { AceClaimAction, AceClaimScope } from "./types";
import {
  enableScopeLoading,
  disableScopeLoading,
} from "../scope-loading/actions";
import { ScopeErrorAction } from "../scope-error/types";
import { setClaimInfo } from "./actions";
import RouteNames from "../../routes/type";
import { navigateTo } from "../../helper";
import {
  mapApiAceClaimItem,
  MapApiAceDamage,
  nextStepRedirect,
} from "./helpers";
import { RootState } from "../root-reducer";
import * as API from "../../service/api";
import moment from "moment";
import { mapApiAcePlan } from "../user/helpers";
import axios from "axios";
import toast from "react-hot-toast";

function* createClaimFlow() {
  while (true) {
    const { payload } = yield take(AceClaimAction.CREATE_CLAIM);
    const { warranty_id } = payload;
    try {
      yield put(enableScopeLoading(AceClaimScope));
      const plan = yield select((state) =>
        state.user.plans.find((item) => item.id === warranty_id)
      );

      const currentDateTime = moment().format("YYYY-MM-DD");
      const data = yield call(API.fileAceClaim.insertAceCliam, {
        warrentyId: String(warranty_id),
        damagedate: currentDateTime,
        claimdate: currentDateTime,
        allowSMS: true,
      });
      const claimId = data?.claim_id;
      yield put(
        setClaimInfo({
          claimId,
          customerId: plan?.customerId || "",
        })
      );
      yield navigateTo(
        `/${RouteNames.CREATE_ACE_CLAIM}?step=2&claimId=${claimId}&warranty_id=${warranty_id}&cust_id=${plan.customerId}`
      );
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: AceClaimScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(AceClaimScope));
    }
  }
}

function* insertAceClaimItemsFlow() {
  while (true) {
    const request = yield take(AceClaimAction.INSERT_CLAIM_ITEMS);
    yield put(enableScopeLoading(AceClaimScope));

    try {
      const requestedData = request.payload;

      const existingClaimItems = yield select(
        (state: RootState) => state.aceClaim.claim_items || []
      );
      for (const payload of requestedData) {
        yield call(API.fileAceClaim.insertAceClaimItems, payload);
      }
      nextStepRedirect(4);
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: AceClaimScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(AceClaimScope));
    }
  }
}

function* insertClaimItemDamagesFlow() {
  while (true) {
    const request = yield take(AceClaimAction.INSERT_CLAIM_ITEM_DAMGES);
    yield put(enableScopeLoading(AceClaimScope));
    try {
      const claimItemDamageData = request?.payload;
      for (const payload of claimItemDamageData) {
        yield call(API.fileAceClaim.insertAceClaimItemsDamage, payload);
      }
      nextStepRedirect(5);
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: AceClaimScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(AceClaimScope));
    }
  }
}

function* submitClaimFlow() {
  while (true) {
    yield take(AceClaimAction.SUBMIT_CLAIM);
    yield put(enableScopeLoading(AceClaimScope));
    try {
      const claimId = yield select((state) => state.aceClaim.claimId);
      const data = yield call(API.fileAceClaim.submitAceClaim, claimId);
      if (data.message === "Succeed") {
        nextStepRedirect(6);
      } else {
        yield put(disableScopeLoading(AceClaimScope));
      }
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: AceClaimScope,
        error,
      });
      yield put(disableScopeLoading(AceClaimScope));
    } finally {
      yield put(disableScopeLoading(AceClaimScope));
    }
  }
}

function* removeClaimItemsFlow() {
  while (true) {
    const request = yield take(AceClaimAction.REMOVE_CLAIM_ITEM);
    yield put(enableScopeLoading(AceClaimScope));
    try {
      const { id: claimItemId } = request.payload;
      const claimId = yield select(
        (state: RootState) => state.aceClaim.claimId
      );
      yield call(API.fileAceClaim.removeAceClaimItems, claimId, claimItemId);
      const data = yield select(
        (state: RootState) => state.aceClaim.claim_items
      );
      const mappeddata = data.filter((item) => item.id !== claimItemId);
      yield put(setClaimInfo({ claim_items: mappeddata }));
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: AceClaimScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(AceClaimScope));
    }
  }
}
function* deleteClaimDamageItemFlow() {
  while (true) {
    const request = yield take(AceClaimAction.DELETE_DAMAGE_ITEM);
    yield put(enableScopeLoading(AceClaimScope));
    try {
      const damageId = request.payload;
      yield call(API.fileAceClaim.removeAceClaimItemDamage, damageId);
      const claim_items = yield select(
        (state: RootState) => state.aceClaim.claim_items
      );
      const updatedClaimItems = claim_items.map((item) => {
        const filteredDamages = (item.damages || []).filter(
          (damage) => damage.id !== damageId
        );
        return {
          ...item,
          damages: filteredDamages,
        };
      });

      yield put(
        setClaimInfo({
          claim_items: updatedClaimItems,
        })
      );
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: AceClaimScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(AceClaimScope));
    }
  }
}

function* fetchClaimInfoFlow() {
  while (true) {
    const request = yield take(AceClaimAction.FETCH_CLAIM_INFO);
    yield put(enableScopeLoading(AceClaimScope));

    try {
      const { claimId, warranty_id, cust_id } = request.payload;
      const oldPlan = yield select((state) => state.aceClaim.plan);
      let plan = oldPlan || {};
      let claim = {};
      let warranty_items = oldPlan?.warrantyItems || [];
      let mapped_claim_items = [];
      let damageItems = [];

      if (warranty_id && !oldPlan.id) {
        const warrantyData = yield call(
          API.warranty.aceGetplanById,
          warranty_id,
          cust_id,
          claimId
        );
        const customerPlan = warrantyData.customers[0];
        plan = mapApiAcePlan(customerPlan);
        warranty_items = plan["warrantyItems"];
      }
      if (claimId) {
        const tempClaim = yield call(API.fileAceClaim.getAceClaim, claimId);
        const {
          items: claim_items = [],
          damageAreasDisp: damages = [],
          documents = [],
        } = tempClaim || {};
        claim = tempClaim;
        mapped_claim_items = claim_items.map((item) => {
          const itemId = item.item_id;
          const mapped = mapApiAceClaimItem(item);
          const itemDamages = (damages || [])
            .filter((dmg) => dmg.item_id === itemId)
            .map(MapApiAceDamage);
          return {
            ...mapped,
            documents_and_photos: documents
              .filter((subitem) => +subitem.gbs_claim_items_id === mapped.id)
              .map(({ doc_name, id }) => ({ id, fileName: doc_name })),
            damages: itemDamages,
          };
        });
      }
      yield put(
        setClaimInfo({
          plan,
          claimId,
          warranty_items,
          claim_items: mapped_claim_items,
          data: claim,
          damage_items: damageItems,
        })
      );
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: AceClaimScope,
        error: "CLAIM_NOT_FOUND",
      });
    } finally {
      yield put(disableScopeLoading(AceClaimScope));
    }
  }
}

function* claimItemUploadFlow() {
  while (true) {
    const request = yield take(AceClaimAction.CLAIM_ITEM_FILE_UPLOAD);
    yield put(enableScopeLoading(AceClaimScope));
    try {
      const { files, claimItemId } = request.payload;
      const claimId = yield select((state) => state.aceClaim.claimId);
      for (const item of files) {
        const { url = "" } = yield call(
          API.fileAceClaim.getPreSignedUrl,
          claimId,
          item.type
        );

        yield call(axios.put, url, item, {
          headers: {
            "Content-Type": item.type,
          },
        });

        const data = new URL(url);
        data.search = "";
        const fileName = data.toString().split("/").pop();

        const res = yield call(API.fileAceClaim.aceClaimItemsSaveInfo, {
          claimId: claimId,
          claimItemsId: claimItemId,
          docName: fileName,
        });
        const updated_claim_items = yield select(
          (state: RootState) => state.aceClaim.claim_items
        );
        const claim_item_data = updated_claim_items.find(
          ({ id }) => id === claimItemId
        );
        const oldNewCombined = [
          ...(claim_item_data.documents_and_photos || []),
          ...res.map(({ id, name }) => ({ id, fileName: name })),
        ];
        yield put(
          setClaimInfo({
            claim_items: updated_claim_items.map((item) =>
              item.id === claim_item_data.id
                ? {
                    ...item,
                    documents_and_photos: oldNewCombined,
                  }
                : item
            ),
          })
        );
      }
      toast.success("files uploaded sucessfully", {
        position: "bottom-center",
      });
    } catch (error) {
      toast.error("file not uploaded.", {
        position: "bottom-center",
      });
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: AceClaimScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(AceClaimScope));
    }
  }
}

function* claimItemDeleteImageFlow() {
  while (true) {
    const request = yield take(AceClaimAction.CLAIM_ITEM_DELETE_IMAGE);
    yield put(enableScopeLoading(AceClaimScope));
    try {
      const { docId, claimItemId } = request.payload;
      const claim_items = yield select(
        (state: RootState) => state.aceClaim.claim_items
      );
      const removeFile = yield call(
        API.fileAceClaim.removeAceClaimImage,
        docId
      );
      const claim_item_data = claim_items.find(({ id }) => id === claimItemId);
      if (removeFile) {
        const removedFileData = claim_item_data?.documents_and_photos?.filter(
          (item) => item.id !== docId
        );
        yield put(
          setClaimInfo({
            claim_items: claim_items.map((item) =>
              item.id === claim_item_data.id
                ? {
                    ...item,
                    documents_and_photos: removedFileData,
                  }
                : item
            ),
          })
        );
        toast.success("file deleted sucessfully", {
          position: "bottom-center",
        });
      } else {
        toast.error("something went wrong, can't delete file.", {
          position: "bottom-center",
        });
      }
    } catch (error) {
      toast.error("file not deleted.", {
        position: "bottom-center",
      });
    } finally {
      yield put(disableScopeLoading(AceClaimScope));
    }
  }
}

export default function* userSaga() {
  yield fork(createClaimFlow);
  yield fork(fetchClaimInfoFlow);
  yield fork(insertClaimItemDamagesFlow);
  yield fork(removeClaimItemsFlow);
  yield fork(insertAceClaimItemsFlow);
  yield fork(submitClaimFlow);
  yield fork(claimItemUploadFlow);
  yield fork(claimItemDeleteImageFlow);
  yield fork(deleteClaimDamageItemFlow);
}
